import React from 'react';
import { Check, Loader2, AlertCircle, RotateCw } from 'lucide-react';
import type { RoaryBuildProgress } from '../../hooks/useRoary';
import roaryMascot from '/assets/Roary-mascot.png';

interface RoaryOperation {
  id: string;
  type: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startedAt?: string;
  completedAt?: string;
}

interface RoaryProgressProps {
  operations: RoaryOperation[];
  isStreaming: boolean;
  currentToolName?: string | null;
  buildProgress?: RoaryBuildProgress | null;
}

// Animated fire icon for in-progress items
const FireIcon: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`relative ${className}`}>
    {/* Main flame */}
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-full h-full text-orange-500 animate-pulse"
    >
      <path d="M12 23c-4.97 0-9-3.58-9-8 0-2.44 1.05-4.93 3.12-7.4.54-.65 1.13-1.26 1.75-1.84L12 2l4.13 3.76c.62.58 1.21 1.19 1.75 1.84C19.95 10.07 21 12.56 21 15c0 4.42-4.03 8-9 8zm0-18.5L8.5 7.75c-.52.47-1 .97-1.45 1.5C5.75 11 5 12.97 5 15c0 3.31 3.13 6 7 6s7-2.69 7-6c0-2.03-.75-4-2.05-5.75-.45-.53-.93-1.03-1.45-1.5L12 4.5z" />
      <path
        d="M12 20c-2.76 0-5-1.79-5-4 0-1.22.55-2.47 1.64-3.72.35-.4.73-.78 1.13-1.13L12 9l2.23 2.15c.4.35.78.73 1.13 1.13C16.45 13.53 17 14.78 17 16c0 2.21-2.24 4-5 4z"
        className="text-yellow-400"
        fill="currentColor"
      />
    </svg>
    {/* Floating sparks */}
    <span className="absolute -top-1 left-1/2 w-1 h-1 bg-orange-400 rounded-full animate-ping opacity-75" />
    <span
      className="absolute -top-2 left-1/3 w-0.5 h-0.5 bg-yellow-400 rounded-full animate-ping opacity-75"
      style={{ animationDelay: '200ms' }}
    />
  </div>
);

// Format tool name to readable description
const formatToolName = (name: string): string => {
  const mappings: Record<string, string> = {
    // Template operations
    list_system_templates: 'Browsing templates',
    create_site_from_template: 'Creating site from template',
    // Site operations
    create_site: 'Creating your website',
    update_site: 'Updating site settings',
    get_site: 'Loading site details',
    list_user_sites: 'Loading your sites',
    update_site_settings: 'Configuring site settings',
    get_available_themes: 'Loading available themes',
    get_site_summary: 'Reviewing site structure',
    // Page operations
    create_page: 'Building a new page',
    update_page: 'Updating page settings',
    delete_page: 'Removing page',
    list_pages: 'Loading pages',
    // Component operations
    add_component: 'Adding content to page',
    add_layout_component: 'Adding to layout',
    update_component: 'Updating section content',
    delete_component: 'Removing section',
    list_components: 'Loading components',
    reorder_components: 'Organizing page sections',
    get_component_schema: 'Preparing component',
    // Layout operations
    create_layout: 'Setting up page layout',
    assign_layout: 'Applying layout to page',
    assign_layout_to_page: 'Applying layout to page',
    list_layouts: 'Loading layouts',
    update_layout: 'Updating layout',
    // Blog operations
    create_blog: 'Setting up your blog',
    create_blog_post: 'Writing blog post',
    update_blog_post: 'Updating blog post',
    list_blog_posts: 'Loading blog posts',
    // Navigation
    update_navbar: 'Configuring navigation menu',
    update_footer: 'Setting up footer',
    // Media
    search_stock_images: 'Finding perfect images',
    upload_image: 'Uploading image',
  };
  return mappings[name] || name.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
};

// Per-page status row for the agentic build view.
const PageStatusRow: React.FC<{
  index: number;
  title: string;
  status: RoaryBuildProgress['pages'][number]['status'];
  componentsUpdated: number;
  attempts: number;
}> = ({ index, title, status, componentsUpdated, attempts }) => {
  let icon: React.ReactNode;
  let label: string;
  let labelClass = 'text-gray-400';
  switch (status) {
    case 'customizing':
      icon = <Loader2 className="w-4 h-4 text-orange-400 animate-spin flex-shrink-0" />;
      label = 'Personalizing…';
      labelClass = 'text-orange-300';
      break;
    case 'verifying':
      icon = <Loader2 className="w-4 h-4 text-amber-300 animate-spin flex-shrink-0" />;
      label = 'Verifying…';
      labelClass = 'text-amber-300';
      break;
    case 'retrying':
      icon = <RotateCw className="w-4 h-4 text-amber-400 animate-spin flex-shrink-0" />;
      label = `Retrying (attempt ${attempts})…`;
      labelClass = 'text-amber-400';
      break;
    case 'verified':
      icon = <Check className="w-4 h-4 text-green-400 flex-shrink-0" />;
      label = componentsUpdated > 0 ? `${componentsUpdated} sections updated` : 'Done';
      labelClass = 'text-green-400';
      break;
    case 'failed':
      icon = <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
      label = 'Needs a second look';
      labelClass = 'text-red-400';
      break;
    case 'pending':
    default:
      icon = <span className="w-4 h-4 rounded-full border border-slate-600 flex-shrink-0" />;
      label = 'Queued';
      break;
  }
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-700/40 last:border-b-0">
      <div className="flex items-center gap-3 min-w-0">
        {icon}
        <span className="text-sm text-gray-200 truncate">
          <span className="text-gray-500 mr-2">{index + 1}.</span>
          {title}
        </span>
      </div>
      <span className={`text-xs ${labelClass} flex-shrink-0`}>{label}</span>
    </div>
  );
};

export const RoaryProgress: React.FC<RoaryProgressProps> = ({
  operations,
  isStreaming,
  currentToolName,
  buildProgress,
}) => {
  const completedCount = operations.filter(op => op.status === 'completed').length;

  // Get the current action to display - prioritize currentToolName from streaming
  const currentAction = currentToolName
    ? formatToolName(currentToolName)
    : operations.find(op => op.status === 'running')
      ? formatToolName(operations.find(op => op.status === 'running')!.type)
      : null;

  const hasAgenticBuild =
    !!buildProgress && (buildProgress.pages.length > 0 || buildProgress.layouts.length > 0);

  // Headline: match the pipeline phase when we have one, otherwise fall back
  // to the generic "Roary is building your website" copy.
  let headline = 'Roary is building your website';
  let subheadline: string | null = null;
  if (hasAgenticBuild) {
    const { plan, phase, currentPageIndex, currentLayoutIndex, pages, layouts } = buildProgress!;
    subheadline = plan ? `${plan.businessName} · ${pages.length} pages planned` : null;
    if (phase === 'scaffolding') {
      headline = 'Laying the foundation';
    } else if (phase === 'building_layouts') {
      const currentLayout = currentLayoutIndex !== null ? layouts[currentLayoutIndex] : undefined;
      headline = currentLayout
        ? `Personalizing ${currentLayout.name} (nav + footer)`
        : 'Personalizing navigation and footer';
    } else if (phase === 'building_pages') {
      const currentPage = currentPageIndex !== null ? pages[currentPageIndex] : undefined;
      headline = currentPage ? `Personalizing “${currentPage.title}”` : 'Personalizing pages';
    } else if (phase === 'completed') {
      headline = 'Finishing up';
    } else if (phase === 'planning') {
      headline = 'Sketching the plan';
    }
  }

  return (
    <div className="h-full flex flex-col items-center justify-center p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-4 mb-4">
          <img
            src={roaryMascot}
            alt="Roary"
            className="w-24 h-24 object-contain animate-roary-float drop-shadow-lg"
          />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{headline}</h2>
        {subheadline && <p className="text-gray-300 text-sm mb-1">{subheadline}</p>}
        {/* The last assistant chat message was previously shown here, but once
            the build starts the user wants the pipeline status, not Roary's
            pre-build confirmation — which also tends to include unrendered
            markdown. Leave the block out. */}
      </div>

      {/* Per-layout pipeline view — shown once the orchestrator reaches
          the layout step. */}
      {hasAgenticBuild && buildProgress!.layouts.length > 0 && (
        <div className="w-full max-w-md bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-4">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">
            Navigation &amp; footer
          </div>
          <div>
            {buildProgress!.layouts.map((l, i) => (
              <PageStatusRow
                key={`layout-${l.layoutId || i}`}
                index={i}
                title={l.name}
                status={l.status}
                componentsUpdated={l.componentsUpdated}
                attempts={l.attempts}
              />
            ))}
          </div>
        </div>
      )}

      {/* Per-page pipeline view (agentic build) */}
      {hasAgenticBuild && buildProgress!.pages.length > 0 && (
        <div className="w-full max-w-md bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-4">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">Pages</div>
          <div>
            {buildProgress!.pages.map((p, i) => (
              <PageStatusRow
                key={`${p.slug}-${i}`}
                index={i}
                title={p.title}
                status={p.status}
                componentsUpdated={p.componentsUpdated}
                attempts={p.attempts}
              />
            ))}
          </div>
        </div>
      )}

      {/* Fallback progress card for non-build turns (discovery, edits, etc.) */}
      {!hasAgenticBuild && (
        <div className="w-full max-w-md bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <div className="mb-6">
            {currentAction ? (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                <Loader2 className="w-6 h-6 text-orange-400 animate-spin flex-shrink-0" />
                <span className="text-white font-medium">{currentAction}</span>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-700/50">
                <FireIcon className="w-6 h-6" />
                <span className="text-gray-400">Roary is working...</span>
              </div>
            )}
          </div>

          {completedCount > 0 && (
            <div className="flex items-center justify-center gap-2 mb-4 text-sm text-gray-400">
              <Check className="w-4 h-4 text-green-400" />
              <span>
                {completedCount} {completedCount === 1 ? 'task' : 'tasks'} completed
              </span>
            </div>
          )}

          <div className="relative">
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all duration-300 relative animate-pulse"
                style={{ width: isStreaming ? '100%' : '0%' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-roary-shine" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating fire particles animation */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-orange-500/40 rounded-full blur-sm animate-roary-float-particle"
            style={{
              left: `${10 + i * 12}%`,
              bottom: '30%',
              animationDuration: `${3 + i * 0.5}s`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};
